import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, ChevronDown, Plus, X, Upload } from 'lucide-react';
import { login, isAuthenticated } from '../lib/auth';
import { getLOGO_URL, getBRAND_NAME, getBRAND_FULL, getBRAND_ACN, getBRAND_TAGLINE, setCurrentCompany } from '../lib/brandConfig';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// ── Create Company Modal ────────────────────────────────────────────────────
function CreateCompanyModal({ onClose, onCreated }) {
  const [companyName, setCompanyName] = useState('');
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [showAdminPw, setShowAdminPw] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoDataUrl, setLogoDataUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef();

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 512 * 1024) {
      setError('Logo must be under 512 KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLogoPreview(ev.target.result);
      setLogoDataUrl(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = companyName.trim();
    const username = adminUsername.trim();
    if (!name) { setError('Company name is required'); return; }
    if (!username) { setError('Admin username is required'); return; }
    if (!adminPassword || adminPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/companies`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, logo_url: logoDataUrl, admin_username: username, admin_password: adminPassword }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to create company');
      }
      const newCompany = await res.json();
      onCreated(newCompany);
    } catch (err) {
      setError(err.message || 'Failed to create company');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-sm mx-4 rounded" style={{ background: '#f8f7f4', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        {/* Modal Header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ background: '#1a1a2e', borderBottom: '1px solid rgba(201,168,76,0.3)' }}>
          <h2 style={{ color: '#f8f7f4', fontSize: '14px', fontWeight: 700, fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            New Company
          </h2>
          <button onClick={onClose} style={{ color: '#6b7280' }} className="hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-5 py-5">
          {error && (
            <div className="flex items-center gap-2 mb-4 px-3 py-2" style={{ background: 'rgba(239,68,68,0.08)', borderRadius: '4px', border: '1px solid rgba(239,68,68,0.2)' }}>
              <AlertCircle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
              <span style={{ color: '#ef4444', fontSize: '12px', fontFamily: "'Manrope', sans-serif" }}>{error}</span>
            </div>
          )}

          {/* Company Name */}
          <div className="mb-4">
            <label className="block mb-1.5" style={{ color: '#6b7280', fontSize: '11px', fontWeight: 600, fontFamily: "'Manrope', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Company Name <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              value={companyName}
              onChange={e => setCompanyName(e.target.value)}
              placeholder="e.g. Coherent Fitout"
              autoFocus
              className="w-full px-3 py-2.5 outline-none"
              style={{ background: '#f3f2ef', border: '1px solid #e5e4df', borderRadius: '4px', color: '#2c2c2c', fontSize: '14px', fontFamily: "'Manrope', sans-serif" }}
              onFocus={e => e.target.style.borderColor = '#c9a84c'}
              onBlur={e => e.target.style.borderColor = '#e5e4df'}
            />
          </div>

          {/* Admin Username */}
          <div className="mb-4">
            <label className="block mb-1.5" style={{ color: '#6b7280', fontSize: '11px', fontWeight: 600, fontFamily: "'Manrope', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Admin Username <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              type="text"
              value={adminUsername}
              onChange={e => setAdminUsername(e.target.value)}
              placeholder="e.g. admin"
              className="w-full px-3 py-2.5 outline-none"
              style={{ background: '#f3f2ef', border: '1px solid #e5e4df', borderRadius: '4px', color: '#2c2c2c', fontSize: '14px', fontFamily: "'Manrope', sans-serif" }}
              onFocus={e => e.target.style.borderColor = '#c9a84c'}
              onBlur={e => e.target.style.borderColor = '#e5e4df'}
            />
          </div>

          {/* Admin Password */}
          <div className="mb-4">
            <label className="block mb-1.5" style={{ color: '#6b7280', fontSize: '11px', fontWeight: 600, fontFamily: "'Manrope', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Admin Password <span style={{ color: '#ef4444' }}>*</span>
              <span style={{ color: '#9ca3af', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}> (min 6 chars)</span>
            </label>
            <div className="relative">
              <input
                type={showAdminPw ? 'text' : 'password'}
                value={adminPassword}
                onChange={e => setAdminPassword(e.target.value)}
                placeholder="Set a strong password"
                className="w-full px-3 py-2.5 pr-10 outline-none"
                style={{ background: '#f3f2ef', border: '1px solid #e5e4df', borderRadius: '4px', color: '#2c2c2c', fontSize: '14px', fontFamily: "'Manrope', sans-serif" }}
                onFocus={e => e.target.style.borderColor = '#c9a84c'}
                onBlur={e => e.target.style.borderColor = '#e5e4df'}
              />
              <button type="button" onClick={() => setShowAdminPw(!showAdminPw)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1" style={{ color: '#6b7280' }} tabIndex={-1}>
                {showAdminPw ? <EyeOff size={15} /> : <Eye size={15} />}
              </button>
            </div>
          </div>

          {/* Logo Upload */}
          <div className="mb-5">
            <label className="block mb-1.5" style={{ color: '#6b7280', fontSize: '11px', fontWeight: 600, fontFamily: "'Manrope', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Company Logo <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional, max 512 KB)</span>
            </label>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
            {logoPreview ? (
              <div className="relative flex items-center gap-3 p-3" style={{ background: '#f3f2ef', border: '1px solid #e5e4df', borderRadius: '4px' }}>
                <img src={logoPreview} alt="Preview" style={{ maxWidth: 80, maxHeight: 60, objectFit: 'contain', borderRadius: '2px' }} />
                <div className="flex-1">
                  <p style={{ fontSize: '12px', color: '#6b7280', fontFamily: "'Manrope', sans-serif" }}>Logo selected</p>
                  <button type="button" onClick={() => fileRef.current.click()} style={{ color: '#c9a84c', fontSize: '12px', fontFamily: "'Manrope', sans-serif" }}>
                    Change
                  </button>
                </div>
                <button type="button" onClick={() => { setLogoPreview(null); setLogoDataUrl(''); }} style={{ color: '#9ca3af' }} className="hover:text-red-500 transition-colors">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current.click()}
                className="w-full flex items-center justify-center gap-2 py-3"
                style={{ background: '#f3f2ef', border: '1px dashed #d1d5db', borderRadius: '4px', color: '#6b7280', fontSize: '13px', fontFamily: "'Manrope', sans-serif', cursor: 'pointer'" }}
              >
                <Upload size={15} />
                Upload Logo
              </button>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5"
              style={{ background: '#f3f2ef', border: '1px solid #e5e4df', borderRadius: '4px', color: '#6b7280', fontSize: '13px', fontWeight: 600, fontFamily: "'Manrope', sans-serif" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !companyName.trim() || !adminUsername.trim() || adminPassword.length < 6}
              className="flex-1 py-2.5"
              style={{
                background: loading || !companyName.trim() || !adminUsername.trim() || adminPassword.length < 6 ? '#9ca3af' : '#1a1a2e',
                borderRadius: '4px', color: '#f8f7f4', fontSize: '13px', fontWeight: 600,
                fontFamily: "'Space Grotesk', sans-serif", letterSpacing: '0.04em',
                cursor: loading || !companyName.trim() || !adminUsername.trim() || adminPassword.length < 6 ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Creating…' : 'Create Company'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Login Page ──────────────────────────────────────────────────────────────
export default function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Multi-tenancy: company list + selected company
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [brand, setBrand] = useState({
    logo_url: '/LOGO.jpg',
    name: 'Revolead Construction',
    acn: '696 557 614',
    tagline: 'Construction Management',
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated()) navigate('/', { replace: true });
  }, [navigate]);

  // Load companies on mount
  useEffect(() => {
    fetch(`${API_BASE}/api/companies`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setCompanies(data);
          const lastSlug = localStorage.getItem('revolead_last_company_slug');
          const found = lastSlug ? data.find(c => c.slug === lastSlug) : data[0];
          if (found) handleCompanySelect(found);
        }
      })
      .catch(err => console.warn('Failed to load companies:', err));
  }, []);

  const handleCompanySelect = async (company) => {
    setSelectedCompany(company);
    localStorage.setItem('revolead_last_company_slug', company.slug);
    try {
      const res = await fetch(`${API_BASE}/api/companies/${company.slug}/brand`);
      const b = await res.json();
      if (b.name) {
        setBrand({
          logo_url: b.logo_url || '/LOGO.jpg',
          name: b.name,
          acn: b.acn || '',
          tagline: b.tagline || '',
        });
      }
    } catch {
      setBrand({
        logo_url: company.logo_url || '/LOGO.jpg',
        name: company.name,
        acn: company.acn || '',
        tagline: company.tagline || '',
      });
    }
  };

  const handleDropdownChange = (e) => {
    const val = e.target.value;
    if (val === '__create__') {
      // Reset select back to current company
      e.target.value = selectedCompany?.slug || '';
      setShowCreateModal(true);
      return;
    }
    const c = companies.find(x => x.slug === val);
    if (c) handleCompanySelect(c);
  };

  const handleCompanyCreated = async (newCompany) => {
    setShowCreateModal(false);
    // Refresh company list and auto-select the new one
    try {
      const res = await fetch(`${API_BASE}/api/companies`);
      const data = await res.json();
      if (Array.isArray(data)) setCompanies(data);
    } catch {}
    handleCompanySelect(newCompany);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!selectedCompany) {
      setError('Please select a company');
      return;
    }
    setLoading(true);
    try {
      await login(username, password, selectedCompany.id);
      setCurrentCompany({
        id: selectedCompany.id,
        name: brand.name,
        slug: selectedCompany.slug,
        acn: brand.acn,
        tagline: brand.tagline,
        logo_url: brand.logo_url,
        color_primary: selectedCompany.color_primary || '#1a1a2e',
        color_accent: selectedCompany.color_accent || '#c9a84c',
      });
      navigate('/', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Login failed. Please check your credentials.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showCreateModal && (
        <CreateCompanyModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCompanyCreated}
        />
      )}

      <div className="min-h-screen flex items-center justify-center" style={{ background: '#e5e4df' }}>
        <div className="w-full max-w-sm mx-4" style={{ background: '#f8f7f4', borderRadius: '4px', boxShadow: '0 4px 24px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          {/* Navy Header with dynamic brand */}
          <div className="flex flex-col items-center justify-center py-8 px-6" style={{ background: '#1a1a2e' }}>
            <img
              src={brand.logo_url}
              alt={brand.name}
              className="mb-4"
              style={{ maxWidth: 160, maxHeight: 160, objectFit: 'contain' }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <h1 className="text-lg tracking-wide" style={{ color: '#f8f7f4', fontFamily: "'Space Grotesk', 'Manrope', sans-serif", fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              {brand.name}
            </h1>
            <p className="mt-1" style={{ color: '#c9a84c', fontFamily: "'Manrope', sans-serif", fontSize: '12px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              {brand.tagline}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-6">
            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 mb-4 px-3 py-2" style={{ background: 'rgba(239,68,68,0.08)', borderRadius: '4px', border: '1px solid rgba(239,68,68,0.2)' }}>
                <AlertCircle size={16} style={{ color: '#ef4444', flexShrink: 0 }} />
                <span style={{ color: '#ef4444', fontSize: '13px', fontFamily: "'Manrope', sans-serif" }}>{error}</span>
              </div>
            )}

            {/* Company Selector */}
            <div className="mb-4">
              <label className="block mb-1.5" style={{ color: '#6b7280', fontSize: '12px', fontWeight: 600, fontFamily: "'Manrope', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Select Company
              </label>
              <div className="relative">
                <select
                  value={selectedCompany?.slug || ''}
                  onChange={handleDropdownChange}
                  className="w-full appearance-none px-3 py-2.5 pr-10 outline-none cursor-pointer"
                  style={{ background: '#f3f2ef', border: '1px solid #e5e4df', borderRadius: '4px', color: '#2c2c2c', fontSize: '14px', fontFamily: "'Manrope', sans-serif" }}
                >
                  <option value="">— Choose a company —</option>
                  {companies.map(c => (
                    <option key={c.id} value={c.slug}>{c.name}</option>
                  ))}
                  <option disabled style={{ color: '#d1d5db' }}>──────────────</option>
                  <option value="__create__" style={{ color: '#c9a84c', fontWeight: 600 }}>
                    + Create New Company
                  </option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" size={16} style={{ color: '#6b7280' }} />
              </div>
            </div>

            {/* Username */}
            <div className="mb-4">
              <label className="block mb-1.5" style={{ color: '#6b7280', fontSize: '12px', fontWeight: 600, fontFamily: "'Manrope', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>Username</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username"
                className="w-full px-3 py-2.5 outline-none" style={{ background: '#f3f2ef', border: '1px solid #e5e4df', borderRadius: '4px', color: '#2c2c2c', fontSize: '14px', fontFamily: "'Manrope', sans-serif", transition: 'border-color 0.2s' }}
                onFocus={(e) => { e.target.style.borderColor = '#c9a84c'; }} onBlur={(e) => { e.target.style.borderColor = '#e5e4df'; }}
                placeholder="Enter username" />
            </div>

            {/* Password */}
            <div className="mb-6">
              <label className="block mb-1.5" style={{ color: '#6b7280', fontSize: '12px', fontWeight: 600, fontFamily: "'Manrope', sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>Password</label>
              <div className="relative">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                  className="w-full px-3 py-2.5 pr-10 outline-none" style={{ background: '#f3f2ef', border: '1px solid #e5e4df', borderRadius: '4px', color: '#2c2c2c', fontSize: '14px', fontFamily: "'Manrope', sans-serif", transition: 'border-color 0.2s' }}
                  onFocus={(e) => { e.target.style.borderColor = '#c9a84c'; }} onBlur={(e) => { e.target.style.borderColor = '#e5e4df'; }}
                  placeholder="Enter password" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1" style={{ color: '#6b7280' }} tabIndex={-1}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button type="submit" disabled={loading || !username || !password || !selectedCompany}
              className="w-full flex items-center justify-center gap-2 py-2.5"
              style={{ background: loading || !username || !password || !selectedCompany ? '#6b7280' : '#1a1a2e', color: '#f8f7f4', borderRadius: '4px', fontSize: '14px', fontWeight: 600, fontFamily: "'Space Grotesk', 'Manrope', sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase', cursor: loading || !username || !password ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1, transition: 'background 0.2s, opacity 0.2s' }}>
              <Lock size={16} strokeWidth={2} />
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Footer */}
          <div className="px-6 pb-4 text-center" style={{ color: '#6b7280', fontSize: '11px', fontFamily: "'Manrope', sans-serif" }}>
            {brand.name} Pty Ltd &middot; ACN {brand.acn}
          </div>
        </div>
      </div>
    </>
  );
}
